import React, {useCallback} from 'react';
import {Alert} from '@mui/material';
import Recaptcha from 'react-recaptcha';

interface RecaptchaProps {
  sitekey: string;
  render: 'explicit' | 'onload';
  verifyCallback?: (response: string) => void;
  expiredCallback?: () => void;
  onloadCallback?: () => void;
}

type RecaptchaInstance = {
  reset: () => void;
  execute: () => void;
};

type RecaptchaComponent = React.ComponentClass<RecaptchaProps> & RecaptchaInstance;

const RecaptchaTyped = Recaptcha as unknown as RecaptchaComponent;

interface SimpleRecaptchaProps {
  verifyCallback?: (response: string) => void;
  expiredCallback?: () => void;
  onloadCallback?: () => void;
  setRef?: (ref: RecaptchaInstance | null) => void;
}

const SimpleRecaptcha = (props: SimpleRecaptchaProps) => {
  const {verifyCallback, expiredCallback, onloadCallback, setRef} = props;
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim();

  // Must stay referentially stable. A fresh callback each render makes React
  // detach (setRef(null)) and re-attach (setRef(instance)) the ref on every
  // commit, so a setRef that stores into state feeds itself renders forever.
  const handleSetRef = useCallback((ref: React.Component<RecaptchaProps> | null) => {
    if (setRef && ref) {
      setRef(ref as unknown as RecaptchaInstance);
    } else if (setRef) {
      setRef(null);
    }
  }, [setRef]);

  if (!siteKey) {
    return <Alert severity="error">Verification is temporarily unavailable. Please try again later.</Alert>;
  }

  return (
    <RecaptchaTyped
      sitekey={siteKey}
      render="explicit"
      ref={handleSetRef}
      verifyCallback={verifyCallback}
      expiredCallback={expiredCallback}
      onloadCallback={onloadCallback}
    />
  );
};

export default SimpleRecaptcha;
